import { Autocomplete } from "@mui/material";
import { Axios } from "api/axios";
import BootstrapTextField from "Common/BootstrapTextField";
import SubmitButton from "Components/Buttons/SubmitButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { useFormik } from "formik";
import {
  buildItemFormData,
  ItemFormValues,
} from "helpers/functions/buildItemFormData";
import { formatLabel } from "helpers/functions/formatLabel";
import { useHandleBack } from "helpers/functions/useHandleBack";
import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { addItem, fetchItemById, updateItem } from "slices/item/thunk";
import {
  formatErrorMessage,
  formatErrorMessageForValidation,
  getFieldErrors,
  isValidationError,
  logError,
} from "utils/errorHandling";
import * as Yup from "yup";

interface TagOption {
  tag_id: string;
  tag_name: string;
}

interface GSTOption {
  gst_id: string;
  gst_percentage: number;
}

interface BrandOption {
  brand_id: string;
  brand_name: string;
}

interface AttributeValueOption {
  manage_values_id: string;
  value: string;
}

type ImageType = File | { url: string };

const normalizeKey = (key: string) => key.replace(/_/g, "-");

const ItemForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [hsnOption, setHSNOption] = useState<[]>([]);
  const [categoryOption, setCategoryOption] = useState<
    Array<{
      category_id: string;
      category_name: string;
      web_per?: number;
      system_per?: number;
      is_mandatory?: boolean;
      attributes: Array<{ id: string; label: string }>;
    }>
  >([]);
  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);

  const [gstOptions, setGSTOptions] = useState<GSTOption[]>([]);
  const [brandOptions, setBrandOptions] = useState<BrandOption[]>([]);
  const dispatch = useDispatch<any>();
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [images, setImages] = useState<ImageType[]>([]);

  const [attributes, setAttributes] = useState<
    Array<{ id: string; label: string }>
  >([]);
  const [attributeOptions, setAttributeOptions] = useState<any>([]);

  // ADD new Manage value
  const [showValueModal, setShowValueModal] = useState(false);
  const [newValue, setNewValue] = useState("");
  const [selectedAttribute, setSelectedAttribute] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showTagModal, setShowTagModal] = useState(false);
  const [newTag, setNewTag] = useState("");

  const [showBrandModal, setShowBrandModal] = useState(false);
  const [newBrand, setNewBrand] = useState("");

  const handleBack = useHandleBack("/item");

  // console.log(tagOptions);

  const [formVisible, setFormVisible] = useState(false);
  const formStyle: React.CSSProperties = {
    opacity: formVisible ? 1 : 0,
    transform: formVisible ? "translateY(10px)" : "translateY(0)",
    transition: "opacity 0.5s ease, transform 0.5s ease",
  };
  useEffect(() => {
    // Trigger fade-in after component mounts
    const timer = setTimeout(() => setFormVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const [initialData, setInitialData] = useState({
    item_id: "",
    item_name: "",
    item_sku: "",
    selling_price: 0,
    purchase_price: 0,
    regular_price: 0,
    web_per: 0,
    system_per: 0,
    sale_price: 0,
    system_sale_price: 0,
    quantity: 0,
    gst: 0,
    hsnId: "",
    hsn_code: "",
    category: "",
    brand: "",
    remarks: "",
    image: "",
    tags: [] as string[], // Properly type as string array for tag_ids
    attributes: [],
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const selectedFiles = Array.from(event.target.files);
      if (images.length + selectedFiles.length > 10) {
        toast.error("You can only upload up to 10 images");
        return;
      }
      if (event.target.files && event.target.files[0]) {
        const file = event.target.files[0];
        setImages([file]); // replace the old image with the new one
      }
    }
  };

  // Fetches All the attributes dropdown values all at once
  const fetchAllAttributeOptions = useCallback(
    async (attributesArray: any[]) => {
      try {
        const results = await Promise.all(
          attributesArray.map(async (att) => {
            try {
              const resp = await Axios.get<{ data: AttributeValueOption[] }>(
                `/api/manage-values/dropdown/?attribute=${att.id}`
              );
              return { attributeId: att.id, options: resp?.data?.data || [] };
            } catch (err) {
              console.error(err);
              return { attributeId: att.id, options: [] };
            }
          })
        );

        const mapped: Record<string, any[]> = {};
        results.forEach((r) => {
          mapped[r.attributeId] = r.options;
        });

        setAttributeOptions(mapped);
      } catch (e) {
        toast.error("Failed to fetch attribute options");
        console.error("Failed to fetch attribute options", e);
      }
    },
    []
  );

  // Fetching the item details if EDIT
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    dispatch(fetchItemById(id))
      .unwrap()
      .then((data: any) => {
        // console.log(data);
        const newInitialData = {
          item_id: data?.item_id,
          item_name: data?.item_name,
          item_sku: data?.item_sku,
          selling_price: data?.selling_price,
          purchase_price: data?.purchase_price,
          web_per: data?.web_per,
          system_per: data?.system_per,
          sale_price: data?.sale_price,
          system_sale_price: data?.system_sale_price,
          regular_price: data?.regular_price,
          quantity: data?.quantity,
          gst: Number(data?.gst || 0),
          hsnId: data.hsn_code,
          hsn_code: data.hsn_code,
          category: data?.category || "",
          brand: data?.brand || data?.brand_id || "",
          remarks: data?.remarks,
          tags: data?.tags,
          image: data?.image || null,
          attributes: data?.attributes || [],
        };

        setInitialData(newInitialData);
      })
      .catch((error: any) => {
        logError(error, "Fetch item");
        const errorMessage = formatErrorMessage(error);
        toast.error(errorMessage);
      })
      .finally(() => setIsLoading(false));
  }, [state?.item_id, id, dispatch]);

  // To set the image preview in edit mode
  // Handle image preview in edit mode
  useEffect(() => {
    if (isEdit && initialData.image) {
      // Set image preview URL in edit mode
      setImages([{ url: initialData.image }]);
    } else {
      // Clear images when not in edit mode or no initial image
      setImages([]);
    }
  }, [isEdit, initialData.image]);

  // Prefetching the dropdown data
  useEffect(() => {
    // HSN fetch
    Axios.get("/api/hsn/hsn-dropdown/")
      .then((success: any) => {
        setHSNOption(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch package list");
      });

    // GST fetch - New API call
    Axios.get("/api/gst/gst-dropdown/")
      .then((success: any) => {
        setGSTOptions(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch GST list");
      });

    // Categories fetch
    Axios.get("/api/category/category-dropdown/")
      .then((success: any) => {
        setCategoryOption(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch Category list");
      });

    // Tags fetch
    Axios.get("/api/tags/tags-dropdown/")
      .then((success: any) => {
        setTagOptions(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch Tag list");
      });

    // Brand fetch
    Axios.get("/api/brand/active-brands-dropdown/")
      .then((success: any) => {
        setBrandOptions(success?.data?.data || []);
      })
      .catch(() => {
        toast.error("Failed to fetch Brand list");
      });
  }, []);

  const transformedAttributes: Record<
    string,
    {
      attribute: string;
      manage_values_id: string;
      value: string;
    }
  > = {};

  initialData.attributes.forEach((attr: any) => {
    const normalizedKey = normalizeKey(attr.attribute_name);
    transformedAttributes[normalizedKey] = {
      attribute: String(attr.attribute_id),
      manage_values_id: String(attr.manage_value_id),
      value: attr.manage_value_name,
    };
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      item_name: initialData.item_name || "",
      item_sku: initialData.item_sku || "",
      selling_price: initialData.selling_price || "",
      purchase_price: initialData.purchase_price || "",
      web_per: initialData.web_per || "",
      system_per: initialData.system_per || "",
      regular_price: initialData.regular_price || "",
      sale_price: initialData.sale_price || "",
      quantity: initialData.quantity || "",
      gst: initialData?.gst || "",
      hsnId: initialData.hsnId || "",
      hsn_code: initialData.hsn_code || "",
      category: initialData.category || "",
      brand: initialData.brand || "",
      remarks: initialData.remarks || "",
      tags: initialData.tags || [],
      image: initialData.image || null,
      attributes: transformedAttributes || {},
    },
    // validate: (values) => {
    //   console.log("Formik validate:", values);
    // },
    validationSchema: Yup.object({
      category: Yup.string().required("Please select a category"),
      item_name: Yup.string().required("Please enter a Item Name").trim(),
      item_sku: Yup.string().required("Please enter SKU").trim(),
      purchase_price: Yup.string()
        .required("Please enter Purchase Price")
        .test(
          "is-number",
          "Purchase Price must be a number",
          (value) => !isNaN(Number(value))
        )
        .test(
          "is-integer",
          "Purchase Price must be a whole number",
          (value) => Number.isInteger(Number(value))
        )
        .test(
          "is-positive",
          "Purchase Price must be positive",
          (value) => Number(value) >= 0
        ),
      // regular_price: Yup.string()
      //   .required("Please enter Regular Price")
      //   .test('is-number', 'Regular Price must be a number', (value) => !isNaN(Number(value)))
      //   .test('is-positive', 'Regular Price must be positive', (value) => Number(value) > 0),
      web_per: Yup.string()
        .required("Please enter %WEB Price")
        .test(
          "is-number",
          "%WEB Price must be a number",
          (value) => !isNaN(Number(value))
        )
        .test(
          "is-positive",
          "%WEB Price must be positive",
          (value) => Number(value) >= 0
        ),
      system_per: Yup.string()
        .required("Please enter %System")
        .test(
          "is-number",
          "%System must be a number",
          (value) => !isNaN(Number(value))
        )
        .test(
          "is-positive",
          "%System must be positive",
          (value) => Number(value) >= 0
        ),
      selling_price: Yup.string()
        .required("Please enter Selling Price")
        .test(
          "is-number",
          "Selling Price must be a number",
          (value) => !isNaN(Number(value))
        )
        .test(
          "is-positive",
          "Selling Price must be positive",
          (value) => Number(value) >= 0
        ),
      quantity: Yup.number()
        .typeError("Quantity must be a number")
        .integer("Quantity must be an integer")
        .min(1, "Quantity must be at least 1"),
      //   .required("Please enter Quantity"),
      gst: Yup.number()
        .nullable()
        .typeError("Please select a valid GST")
        .min(1, "Please select a GST")
        .max(100, "GST must be less than 100")
        .required("Please select a GST"),

      // attributes: Yup.object().test(
      //   'all-attributes-filled',
      //   'All attributes must be filled',
      //   function(value) {
      //     const requiredCount = attributes.length;
      //     if (requiredCount === 0) return true;

      //     if (!value || typeof value !== 'object') return false;

      //     const filledAttributes = Object.values(value).filter(Boolean).length;
      //     return filledAttributes === requiredCount;
      //   }
      // ),
    }),

    onSubmit: async (values) => {
      console.log(values);
      try {
        // Check if attributes are mandatory based on category's is_mandatory field
        const selectedCategory = categoryOption.find(
          (cat) => cat.category_id === values.category
        );
        const isAttributesMandatory = selectedCategory?.is_mandatory === true;

        // Only check attributes if they are mandatory and there are attributes for the selected category
        if (isAttributesMandatory && attributes.length > 0) {
          // Number of required attributes for the selected category
          const requiredAttributeCount = attributes.length;

          // Number of attributes the user has selected
          const selectedAttributeCount = Object.keys(
            values.attributes || {}
          ).length;

          // Check if all required attributes are selected
          if (
            selectedAttributeCount !== requiredAttributeCount ||
            Object.values(values.attributes).some((val) => !val)
          ) {
            toast.error("Please select all required attributes");
            return;
          }
        }

        // console.log(values);

        // Create form data using the utility function
        // Only pass the image if it's a new file upload, not a URL from edit mode
        const imageToUpload =
          images.length > 0 && images[0] instanceof File
            ? images[0]
            : undefined;
        // Ensure proper type conversion for numeric values
        const formValues: ItemFormValues = {
          ...values,
          selling_price: Number(values.selling_price),
          purchase_price: Number(values.purchase_price),
          web_per: Number(values.web_per),
          system_per: Number(values.system_per),
          regular_price: Number(values.regular_price),
          sale_price: Number(values.sale_price),
          quantity: Number(values.quantity),
          gst: Number(values.gst),
          tags: values.tags?.map(String),
          attributes: values.attributes,
        };
        const formData = buildItemFormData(formValues, imageToUpload);

        try {
          let response;
          if (isEdit) {
            response = await dispatch(
              updateItem({ id: initialData?.item_id, formData })
            ).unwrap();
          } else {
            response = await dispatch(addItem(formData)).unwrap();
          }

          toast.success(
            response?.message ||
              (isEdit ? "Item updated successfully" : "Item added successfully")
          );
          handleBack();
        } catch (error: any) {
          logError(error, "Form submission");

          // Handle validation errors with field-specific messages
          if (isValidationError(error)) {
            const fieldErrors = getFieldErrors(error);

            // Set field errors in formik
            Object.keys(fieldErrors).forEach((field) => {
              if (field in formik.values) {
                formik.setFieldError(field, fieldErrors[field]);
              }
            });

            const errorMessage =
              formatErrorMessageForValidation(error) ||
              "Please fix the validation errors above";
            toast.error(errorMessage);
          } else {
            // Handle other error types
            const errorMessage = formatErrorMessage(error);
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Unexpected error:", error);
        toast.error("An unexpected error occurred. Please try again.");
      }
    },
  });

  // Prefill tags when in edit mode and tagOptions are ready
  useEffect(() => {
    if (isEdit && initialData.tags?.length > 0 && tagOptions.length > 0) {
      // The initialData.tags already contains the tag_ids, so just set them directly
      formik.setFieldValue("tags", initialData.tags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, initialData.tags, tagOptions]);

  // Calculate sale_price and selling_price when purchase_price, web_per, or system_per change
  useEffect(() => {
    const purchasePrice = Number(formik.values.purchase_price) || 0;
    const webPer = Number(formik.values.web_per) || 0;
    const systemPer = Number(formik.values.system_per) || 0;

    // Sale Price = purchase_price × web_per
    const salePrice = Math.round(purchasePrice * ((webPer + 100) / 100));
    formik.setFieldValue("sale_price", salePrice);

    // Selling Price = system_per × purchase_price
    const sellingPrice = Math.round(purchasePrice * ((systemPer + 100) / 100));
    formik.setFieldValue("selling_price", sellingPrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formik.values.purchase_price,
    formik.values.web_per,
    formik.values.system_per,
  ]);

  // Setup initial category attributes when data is loaded
  useEffect(() => {
    if (initialData.category && categoryOption.length > 0) {
      const selectedCategory = categoryOption.find(
        (cat) => cat.category_id === initialData.category
      );

      if (selectedCategory?.attributes) {
        setAttributes(selectedCategory.attributes);
        fetchAllAttributeOptions(selectedCategory.attributes);
      }
    }
  }, [initialData.category, categoryOption, fetchAllAttributeOptions]);

  // Transform attribute data for formik when initialData.attributes changes
  useEffect(() => {
    if (
      !Array.isArray(initialData.attributes) ||
      initialData.attributes.length === 0
    )
      return;

    const transformedAttributes: Record<string, any> = {};
    initialData.attributes.forEach((attr: any) => {
      const normalizedKey = normalizeKey(attr.attribute_name);
      transformedAttributes[normalizedKey] = {
        attribute: attr.attribute_id,
        manage_values_id: attr.manage_value_id,
        value: attr.manage_value_name,
      };
    });

    // Set the initial values directly in formik's initialValues
    setInitialData((prev) => ({
      ...prev,
      transformedAttributes,
    }));
  }, [initialData.attributes]);

  // function to handle new value addition
  const handleAddNewValue = async (attributeId: string, value: string) => {
    try {
      setIsSubmitting(true);
      const response = await Axios.post("/api/manage-values/create/", {
        attribute: attributeId,
        value: value.trim(),
      });

      if (response.data) {
        // Add the new value to the attribute options
        const newOption = {
          manage_values_id: response.data.data.manage_values_id,
          value: value.trim(),
        };

        // Update the attribute options
        setAttributeOptions((prev: any) => ({
          ...prev,
          [attributeId]: [...(prev[attributeId] || []), newOption],
        }));

        // Auto-select the new value
        const apiKey = selectedAttribute
          ? normalizeKey(selectedAttribute.label)
          : "";
        formik.setFieldValue(`attributes.${apiKey}`, {
          attribute: attributeId,
          manage_values_id: response.data.data.manage_values_id,
          value: value.trim(),
        });

        toast.success("Value added successfully");
        setShowValueModal(false);
        setNewValue("");
      }
    } catch (error: any) {
      console.error("Error adding new value:", error);
      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNewTagValue = async (newTagValue: string) => {
    try {
      setIsSubmitting(true);
      const response = await Axios.post("/api/tags/create/", {
        tag_name: newTagValue,
      });

      if (response.data) {
        // Add the new value to the Tag options
        const newTag = {
          tag_id: response.data.data.tag_id,
          tag_name: response.data.data.tag_name,
        };

        // console.log(response);

        setTagOptions((prev: any) => [...prev, newTag]);

        // Update formik values with the new tag's ID
        formik.setFieldValue("tags", [
          ...(formik.values.tags || []),
          newTag.tag_id,
        ]);

        toast.success("New Tag added successfully");
        setShowTagModal(false);
        setNewTag("");
      }
    } catch (error: any) {
      console.error("Error adding Tag:", error);
      toast.error(error.response.data.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddNewBrand = async (newBrandValue: string) => {
    try {
      setIsSubmitting(true);
      const response = await Axios.post("/api/brand/create/", {
        brand_name: newBrandValue.trim(),
        remark: null,
      });

      if (response.data) {
        // Add the new brand to the Brand options
        const newBrandOption = {
          brand_id: response.data.data?.brand_id || response.data.data?.id,
          brand_name: response.data.data?.brand_name || newBrandValue.trim(),
        };

        setBrandOptions((prev: any) => [...prev, newBrandOption]);

        // Auto-select the newly added brand
        formik.setFieldValue("brand", newBrandOption.brand_id);

        toast.success("New Brand added successfully");
        setShowBrandModal(false);
        setNewBrand("");
      }
    } catch (error: any) {
      console.error("Error adding Brand:", error);
      
      // Handle validation errors
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        // Extract all validation error messages
        const errorMessages: string[] = [];
        Object.keys(errors).forEach((field) => {
          if (Array.isArray(errors[field])) {
            errorMessages.push(...errors[field]);
          } else {
            errorMessages.push(errors[field]);
          }
        });
        
        // Display the first validation error or all errors
        if (errorMessages.length > 0) {
          toast.error(errorMessages[0] || "Validation error");
        } else {
          toast.error("Validation error");
        }
      } else {
        // Keep the current handling for other errors
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to add brand";
        toast.error(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-content px-4">
      <div
        style={{
          background: "#fff",
          borderRadius: "10px",
          padding: "30px",
          boxShadow: "0 0 15px rgba(0,0,0,0.05)",
        }}
      >
        {/* Heading Part */}
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
          {/* Left: Back icon and heading */}
          <div className="d-flex align-items-center gap-2">
            <span
              style={{ fontSize: "24px", cursor: "pointer" }}
              onClick={handleBack}
            >
              ←
            </span>
            <h5 className="mb-0 fw-bold">
              {isEdit ? "Edit Item" : "Add Item"}
            </h5>
          </div>

          {/* Right: Breadcrumb */}
          <div className="text-muted">
            <span
              className="text-muted"
              style={{ fontSize: "0.9rem", cursor: "pointer" }}
              onClick={handleBack}
            >
              Item
            </span>{" "}
            &gt;{" "}
            <span
              className="text-muted"
              style={{ fontSize: "0.9rem", cursor: "pointer" }}
            >
              {id ? "Edit Item" : "Add Item"}
            </span>{" "}
          </div>
        </div>

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Form
            style={isEdit ? formStyle : {}}
            autoComplete="off"
            onSubmit={formik.handleSubmit}
            // style={{ paddingBottom: "4rem" }}
          >
            <Row>
              {/* Category */}
              <Col lg={3}>
                <div className="mb-4">
                  <Form.Label>
                    Category <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={categoryOption}
                    getOptionLabel={(option: any) =>
                      typeof option === "string" ? option : option.category_name
                    }
                    value={
                      categoryOption?.find(
                        (unit: any) =>
                          unit.category_id === formik.values.category
                      ) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: any) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option: any) =>
                          typeof option === "string" ? option : option.category_name
                      )
                    }
                    onChange={(event, val: any) => {
                      const newCatId = val?.category_id || "";
                      formik.setFieldValue("category", newCatId);
                      formik.setFieldValue("attributes", {});
                      setAttributeOptions({});
                      setAttributes([]);
                      if (val) {
                        setAttributes(val.attributes);
                        fetchAllAttributeOptions(val.attributes);

                        // Auto-fill web_per and system_per from category if available
                        if (val.web_per !== undefined && val.web_per !== null) {
                          formik.setFieldValue("web_per", val.web_per);
                        }
                        if (
                          val.system_per !== undefined &&
                          val.system_per !== null
                        ) {
                          formik.setFieldValue("system_per", val.system_per);
                        }
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("category", true)}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Category"
                        error={
                          formik.touched.category &&
                          Boolean(formik.errors.category)
                        }
                        helperText={
                          formik.touched.category && formik.errors.category
                        }
                        fullWidth
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />
                </div>
              </Col>

              {/* Item Name */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Item Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="item_name"
                    placeholder="Enter Item Name"
                    value={formik.values.item_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.item_name && formik.touched.item_name
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.item_name}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/*  Item SKU */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Item SKU <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="item_sku"
                    placeholder="Enter Item SKU Name"
                    value={formik.values.item_sku}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.item_sku && formik.touched.item_sku
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.item_sku}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* Brand */}
              <Col lg={3}>
                <div className="mb-4">
                  <div className="mb-2">
                    <div className="d-flex justify-content-between align-items-start flex-wrap">
                      <Form.Label className="mb-0 fw-semibold d-flex align-items-center">
                        Brand
                      </Form.Label>

                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="py-0 mt-1 mt-lg-0"
                        onClick={() => {
                          setShowBrandModal(true);
                        }}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                  <Autocomplete
                    options={brandOptions}
                    getOptionLabel={(option: BrandOption) =>
                      typeof option === "string" ? option : option.brand_name
                    }
                    value={
                      brandOptions?.find(
                        (brand: BrandOption) =>
                          brand.brand_id === formik.values.brand
                      ) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: BrandOption) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option: BrandOption) =>
                          typeof option === "string" ? option : option.brand_name
                      )
                    }
                    onChange={(event, val: BrandOption | null) => {
                      formik.setFieldValue("brand", val?.brand_id || "");
                    }}
                    onBlur={() => formik.setFieldTouched("brand", true)}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        placeholder="Select Brand"
                        error={
                          formik.touched.brand && Boolean(formik.errors.brand)
                        }
                        helperText={
                          formik.touched.brand && formik.errors.brand
                        }
                        fullWidth
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />
                </div>
              </Col>

              {/* Purchase Price */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Purchase Price <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    step="1"
                    name="purchase_price"
                    placeholder="Enter Purchase Price"
                    value={formik.values.purchase_price}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("purchase_price", value);
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && !isNaN(Number(value))) {
                        formik.setFieldValue("purchase_price", Math.round(Number(value)));
                      }
                      formik.handleBlur(e);
                    }}
                    isInvalid={
                      !!formik.errors.purchase_price &&
                      formik.touched.purchase_price
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.purchase_price}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* Regular Price */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>Regular Price (MRP)</Form.Label>
                  <Form.Control
                    type="number"
                    step="1"
                    min="0"
                    name="regular_price"
                    placeholder="Enter Regular Price"
                    value={formik.values.regular_price}
                    onChange={(e) => {
                      const value = e.target.value;
                      formik.setFieldValue("regular_price", value);
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      if (value && !isNaN(Number(value))) {
                        formik.setFieldValue("regular_price", Math.round(Number(value)));
                      }
                      formik.handleBlur(e);
                    }}
                    isInvalid={
                      !!formik.errors.regular_price &&
                      formik.touched.regular_price
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.regular_price}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* %WEB */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    %WEB <span className="text-danger">*</span>
                    <small className="text-muted ms-2">
                      (Auto-filled from category)
                    </small>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="web_per"
                    placeholder="Enter %WEB Price"
                    value={formik.values.web_per}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.web_per && formik.touched.web_per
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.web_per}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* %System */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    %System <span className="text-danger">*</span>
                    <small className="text-muted ms-2">
                      (Auto-filled from category)
                    </small>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="system_per"
                    placeholder="Enter System %"
                    value={formik.values.system_per}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.system_per && formik.touched.system_per
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.system_per}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* Sale Price (Web Selling Price) - Calculated */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Sale Price (Web){" "}
                    <span className="text-muted">(Auto calculated)</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    readOnly
                    name="sale_price"
                    placeholder="Auto calculated"
                    value={formik.values.sale_price || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      backgroundColor: "#f8f9fa",
                      color: "#6c757d",
                      cursor: "not-allowed",
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  />
                </div>
              </Col>

              {/* Selling Price (System Selling Price) - Calculated */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Selling Price (System){" "}
                    <span className="text-muted">(Auto calculated)</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    readOnly
                    name="selling_price"
                    placeholder="Auto calculated"
                    value={formik.values.selling_price || ""}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    style={{
                      backgroundColor: "#f8f9fa",
                      color: "#6c757d",
                      cursor: "not-allowed",
                      fontFamily: 'Poppins, sans-serif',
                    }}
                  />
                </div>
              </Col>

              {/* HSN code */}
              <Col lg={3}>
                <div className="mb-4">
                  <Form.Label>
                    HSN Code
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Autocomplete
                    options={hsnOption}
                    getOptionLabel={(option: any) =>
                      typeof option === "string" ? option : option.hsn_code
                    }
                    value={
                      hsnOption?.find(
                        (unit: any) => unit.hsn_code_id === formik.values.hsnId
                      ) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: any) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option: any) =>
                          typeof option === "string" ? option : option.hsn_code
                      )
                    }
                    onChange={(event, val) => {
                      if (val) {
                        // console.log(val);
                        // Store both HSN ID and code
                        formik.setFieldValue("hsnId", val.hsn_code_id);
                        formik.setFieldValue("hsn_code", val.hsn_code);

                        // Get GST ID and percentage from HSN option
                        const gstId = val.gst;
                        const gstPercentage =
                          val.gst?.gst__gst_percentage || val.gst_percentage;

                        // Set both GST ID and percentage
                        formik.setFieldValue("gst_id", gstId);
                        formik.setFieldValue("gst", gstPercentage);

                        // Find and set the corresponding GST option
                        const matchingGstOption: any = gstOptions.find(
                          (g: any) => g.gst_id === gstId
                        );
                        if (matchingGstOption) {
                          formik.setFieldValue(
                            "gst",
                            matchingGstOption.gst_percentage
                          );
                        }
                      } else {
                        formik.setFieldValue("hsnId", "");
                        formik.setFieldValue("hsn_code", "");
                        formik.setFieldValue("gst_id", "");
                        formik.setFieldValue("gst", "");
                      }
                    }}
                    onBlur={() => formik.setFieldTouched("hsnId", true)}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        name="hsnId"
                        placeholder="Select HSN Code"
                        error={
                          formik.touched.hsnId && Boolean(formik.errors.hsnId)
                        }
                        helperText={formik.touched.hsnId && formik.errors.hsnId}
                        fullWidth
                        size="small"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          background: "#FFF",
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ced4da",
                        },
                      },
                      "& .MuiAutocomplete-input": {
                        fontSize: "14px",
                      },
                      "& .MuiAutocomplete-option": {
                        fontSize: "14px",
                      },
                    }}
                  />

                  <Form.Control.Feedback type="invalid">
                    {formik.errors.hsnId}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* GST */}
              <Col lg={3}>
                <div className="mb-4">
                  <Form.Label>
                    GST % <span className="text-danger">*</span>
                  </Form.Label>
                  <Autocomplete
                    options={gstOptions}
                    getOptionLabel={(option: GSTOption) =>
                      option ? `${option.gst_percentage}%` : ""
                    }
                    value={
                      gstOptions.find(
                        (g) =>
                          Number(g.gst_percentage) === Number(formik.values.gst)
                      ) || null
                    }
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: GSTOption) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option: GSTOption) =>
                          option ? `${option.gst_percentage}%` : ""
                      )
                    }
                    onChange={(event, val: GSTOption | null) => {
                      formik.setFieldValue(
                        "gst",
                        val ? Number(val.gst_percentage) : null
                      );
                    }}
                    onBlur={() => formik.setFieldTouched("gst", true)}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        name="gst"
                        placeholder="Select GST"
                        error={formik.touched.gst && Boolean(formik.errors.gst)}
                        helperText={formik.touched.gst && formik.errors.gst}
                        fullWidth
                        size="small"
                        sx={autocompleteDropdownStyles.inputSx}
                      />
                    )}
                  />

                  <Form.Control.Feedback type="invalid">
                    {formik.errors.gst}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* Quantity */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>
                    Quantity
                    {/* <span className="text-danger">*</span> */}
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    name="quantity"
                    placeholder="Enter Quantity"
                    value={formik.values.quantity}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.quantity && formik.touched.quantity
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.quantity}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* Remark */}
              <Col lg={3}>
                <div className="mb-3">
                  <Form.Label>Remark</Form.Label>
                  <Form.Control
                    type="text"
                    name="remarks"
                    placeholder="Enter Remark"
                    value={formik.values.remarks}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={
                      !!formik.errors.remarks && formik.touched.remarks
                    }
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.remarks}
                  </Form.Control.Feedback>
                </div>
              </Col>

              {/* Tags */}
              <Col lg={6} md={12}>
                <div className="mb-3 mt-1">
                  <div className="mb-1">
                    <div className="d-flex justify-content-between align-items-start flex-wrap">
                      <Form.Label className="mb-0 fw-semibold d-flex align-items-center">
                        Tags
                        {/* <span className="text-danger ms-1">*</span> */}
                      </Form.Label>

                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="py-0 mt-1 mt-lg-0"
                        onClick={() => {
                          setShowTagModal(true);
                        }}
                      >
                        +
                      </Button>
                    </div>
                  </div>
                  <Autocomplete
                    multiple
                    options={tagOptions}
                    getOptionLabel={(option: TagOption) => option.tag_name}
                    value={tagOptions.filter((opt: TagOption) =>
                      (formik.values.tags || []).includes(opt.tag_id)
                    )}
                    ListboxProps={autocompleteDropdownStyles.ListboxProps}
                    renderOption={(props, option: TagOption) =>
                      autocompleteDropdownStyles.renderOption(
                        props,
                        option,
                        (option: TagOption) => option.tag_name
                      )
                    }
                    onChange={(event, val: TagOption[]) => {
                      formik.setFieldValue(
                        "tags",
                        val.map((v: TagOption) => v.tag_id)
                      );
                    }}
                    onBlur={() => formik.setFieldTouched("tags", true)}
                    renderInput={(params) => (
                      <BootstrapTextField
                        {...params}
                        name="tags"
                        placeholder="Select Tags"
                        error={Boolean(
                          formik.touched.tags && formik.errors.tags
                        )}
                        helperText={
                          formik.touched.tags && (formik.errors.tags as any)
                        }
                        fullWidth
                        size="small"
                        sx={{
                          ...autocompleteDropdownStyles.inputSx,
                          background: "#FFF",
                        }}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&:hover fieldset": {
                          borderColor: "#ced4da",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#ced4da",
                        },
                      },
                      "& .MuiAutocomplete-input": {
                        fontSize: "14px",
                      },
                      "& .MuiAutocomplete-option": {
                        fontSize: "14px",
                      },
                    }}
                  />
                </div>
              </Col>

              {/* Product Image */}
              <Col lg={12}>
                <div className="mb-3">
                  <Form.Label>Product Image (Optional)</Form.Label>
                  <div
                    className="border rounded d-flex flex-column align-items-center justify-content-center"
                    style={{
                      width: "80px",
                      height: "80px",
                      cursor: "pointer",
                      background: "#ced4da",
                      borderStyle: "dashed",
                      borderRadius: "20px",
                    }}
                    onClick={() =>
                      document.getElementById("fileUpload")?.click()
                    }
                  >
                    <span style={{ fontSize: "24px" }}>＋</span>
                    <small>Upload</small>
                  </div>
                  <input
                    id="fileUpload"
                    type="file"
                    accept="image/png, image/jpeg"
                    multiple
                    hidden
                    onChange={handleFileChange}
                  />
                  {/* <small className="text-muted">
                  Upto 10 images. PNG or JPG. Recommended 1024×1024 px or 1:1
                  ratio.
                </small> */}

                  {/* Preview Thumbnails */}
                  <div className="d-flex flex-wrap mt-3 gap-2">
                    {images.map((img, index) => (
                      <div
                        key={index}
                        className="position-relative"
                        style={{
                          width: "100px",
                          height: "100px",
                          background: "#f8f9fa",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <img
                          src={
                            (img as File).name
                              ? URL.createObjectURL(img as File)
                              : (img as any).url
                          }
                          alt="preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setImages((prev) =>
                              prev.filter((_, i) => i !== index)
                            )
                          }
                          className="btn btn-sm btn-danger position-absolute top-0 end-0"
                          style={{ borderRadius: "50%" }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Dynamic Attributes */}
            <Row>
              {attributes.length > 0 && (
                <>
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <h5 className="mb-0 fw-bold">Attributes</h5>
                  </div>
                  {attributes.map((obj: any) => {
                    const apiKey = normalizeKey(obj.label);
                    const options = attributeOptions[obj.id] || [];
                    
                    // Check if attributes are mandatory based on category's is_mandatory field
                    const selectedCategory = categoryOption.find(
                      (cat) => cat.category_id === formik.values.category
                    );
                    const isAttributesMandatory = selectedCategory?.is_mandatory === true;

                    return (
                      <Col lg={3} key={obj.id}>
                        <div className="mb-4">
                          <div className="mb-1">
                            <div className="d-flex justify-content-between align-items-start flex-wrap">
                              <Form.Label className="mb-0 fw-semibold d-flex align-items-center">
                                {formatLabel(obj.label)}
                                {isAttributesMandatory && (
                                  <span className="text-danger ms-1">*</span>
                                )}
                              </Form.Label>

                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="py-0 mt-1 mt-lg-0"
                                onClick={() => {
                                  setSelectedAttribute(obj);
                                  setShowValueModal(true);
                                }}
                              >
                                +
                              </Button>
                            </div>
                          </div>

                          <Autocomplete
                            options={options}
                            getOptionLabel={(option: any) =>
                              option?.value || ""
                            }
                            value={formik.values.attributes?.[apiKey] || null}
                            ListboxProps={autocompleteDropdownStyles.ListboxProps}
                            renderOption={(props, option: any) =>
                              autocompleteDropdownStyles.renderOption(
                                props,
                                option,
                                (option: any) => option?.value || ""
                              )
                            }
                            onChange={(_, val: any) => {
                              if (val) {
                                formik.setFieldValue(`attributes.${apiKey}`, {
                                  attribute: obj.id, // Store the attribute ID
                                  manage_values_id: val.manage_values_id,
                                  value: val.value,
                                });
                              } else {
                                formik.setFieldValue(
                                  `attributes.${apiKey}`,
                                  null
                                );
                              }
                            }}
                            onBlur={() =>
                              formik.setFieldTouched(
                                `attributes.${apiKey}`,
                                true
                              )
                            }
                            renderInput={(params) => (
                              <BootstrapTextField
                                {...params}
                                placeholder={`Select ${formatLabel(obj.label)}`}
                                error={Boolean(
                                  formik.touched.attributes?.[apiKey] &&
                                    formik.errors.attributes?.[apiKey]
                                )}
                                helperText={
                                  formik.touched.attributes?.[apiKey]
                                    ? String(
                                        formik.errors.attributes?.[apiKey] ?? ""
                                      )
                                    : ""
                                }
                                fullWidth
                                size="small"
                                sx={autocompleteDropdownStyles.inputSx}
                              />
                            )}
                          />
                        </div>
                      </Col>
                    );
                  })}
                </>
              )}
            </Row>

            <div className="d-flex justify-content-center gap-3 py-3">
              <Button
                variant="outline-secondary"
                style={{
                  color: "#878899",
                  borderColor: "#B0BAC9",
                  backgroundColor: "transparent",
                }}
                onClick={handleBack}
              >
                Cancel
              </Button>

              {/* Reusable submit button with spinner */}
              <SubmitButton isSubmitting={formik.isSubmitting} isEdit={!!id} />
            </div>
          </Form>
        )}
      </div>

      {/* Add Value Modal */}
      <Modal
        show={showValueModal}
        onHide={() => {
          setNewValue("");
          setShowValueModal(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Add New{" "}
            {selectedAttribute ? formatLabel(selectedAttribute.label) : ""}{" "}
            Value
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Value</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter new value"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowValueModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!newValue.trim() || isSubmitting}
            onClick={() => {
              if (selectedAttribute && newValue.trim()) {
                handleAddNewValue(selectedAttribute.id, newValue);
              }
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add Value"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add New Tag Modal */}
      <Modal
        show={showTagModal}
        onHide={() => {
          setShowTagModal(false);
          setNewTag("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Tag</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Tag</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter new Tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTagModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!newTag.trim() || isSubmitting}
            onClick={() => {
              handleAddNewTagValue(newTag);
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add New Brand Modal */}
      <Modal
        show={showBrandModal}
        onHide={() => {
          setShowBrandModal(false);
          setNewBrand("");
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add New Brand</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Brand Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter new Brand Name"
              value={newBrand}
              onChange={(e) => setNewBrand(e.target.value)}
              style={{ fontFamily: 'Poppins, sans-serif' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowBrandModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!newBrand.trim() || isSubmitting}
            onClick={() => {
              handleAddNewBrand(newBrand);
            }}
          >
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              "Add"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ItemForm;
